import { FoodsIcon, FormUnit } from "@foodpilot/foods";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { DefaultValueButton } from "../Common/DefaultValueButton";
import { FormTooltip } from "../Common/FormTooltip";
import { GenericValueCheckbox } from "../Common/GenericValueCheckbox";
import { UploadFileAndComment } from "../PropertyActions/UploadFileAndComment";
import { Property } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { PostElement, UnitConversion } from "src/api/client-api/post/postSchema";
import { formatUnitAbbreviation } from "src/utils/formatting";
import { manageTooltipText } from "src/utils/tooltips";
import { sanitizeRichTextHtml } from "src/components/HTMLBox/sanitizeRichTextHtml";
import { usePropertyContext } from "../context/usePropertyContext";
import { useTranslation } from "react-i18next";
import { DimensionValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { getDisplayValueForScore } from "src/utils/scores";
import { UnitConverterButton } from "./UnitConverterButton";
import { UnitConverterSection } from "./UnitConverterSection";
import { PropertyUpdateOutcome } from "../context/context";
import { buildUnitConverterOptions } from "src/utils/buildUnitConverterOptions";
import { useQuestionnaireFormContext } from "../../context/useQuestionnaireForm";

type LayoutOptions = {
    defaultValue: string | null;
    genericValue: string | null;
    isGenericValueSelected: boolean;
    dimensionValue: DimensionValue | undefined;
    forceHideScore?: boolean;
    setDefaultValue: () => void;
    setGenericValue: () => void;
    hideUnit?: boolean;
    unitConverter?: {
        key: string;
        onApply: (canonicalValue: string, unitConversion: UnitConversion) => Promise<PropertyUpdateOutcome>;
    };
};

type Props = {
    property: Property;
    element: PostElement | undefined;
    layoutOptions: LayoutOptions;
    children: ReactNode;
};
export const FieldValueLayout = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { property, element, children, layoutOptions } = props;
    const { canEdit, score, scoreUnit } = usePropertyContext();
    const { openUnitConverterKey } = useQuestionnaireFormContext();

    const unit = layoutOptions.hideUnit ? null : formatUnitAbbreviation(property.unit);

    const { isGenericValueSelected, defaultValue, genericValue, dimensionValue, forceHideScore } = layoutOptions;

    const shouldDisplayScore = !forceHideScore;
    const hasDefaultValue = defaultValue !== null;
    const hasGenericValue = genericValue !== null;

    const canEditProperty = !property.readonly && canEdit;

    const unitConverter = layoutOptions.unitConverter;
    const unitConverterOptions =
        unitConverter ? buildUnitConverterOptions({ converter: property.converter, unit: property.unit }) : [];
    const showsUnitConverterButton =
        unitConverter !== undefined && (unitConverterOptions.length > 0 || element?.unitConversion != null);
    const unitConverterDisabled = !canEditProperty || isGenericValueSelected;
    const isUnitConverterOpen =
        unitConverter !== undefined && openUnitConverterKey === unitConverter.key && !unitConverterDisabled;

    const shouldDisplayDefaultButton =
        canEditProperty && defaultValue !== null && !isGenericValueSelected && hasDefaultValue;
    const shouldDisplayGenericButton = hasGenericValue;

    const tooltipText = manageTooltipText((coalescedText) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(coalescedText) }} />;
    }, property);

    const propertyScoreValue = OlapScoresService.normalizeNumericalScore(dimensionValue?.score.value);
    const scoreProperty =
        propertyScoreValue !== null ?
            {
                score:
                    shouldDisplayScore !== false ? getDisplayValueForScore(propertyScoreValue, score.precision) : null,
                unit: scoreUnit,
            }
        :   undefined;
    const displayScore =
        shouldDisplayScore && scoreProperty && scoreProperty.score !== null && scoreProperty.score !== 0;

    const hasOption = ["radio", "checkbox"].includes(property.field?.type ?? "");
    const hasSelect = ["select"].includes(property.field?.type ?? "");
    const hasLongText = property.field?.type === "long_text";
    return (
        <Stack flexDirection={"column"} gap={theme.spacing(1)}>
            <Stack flexDirection={"row"} justifyContent={"space-between"} gap={theme.spacing(2)}>
                <Stack
                    flexDirection={"column"}
                    gap={theme.spacing(1)}
                    width={"100%"}
                    sx={{
                        containerType: "inline-size",
                    }}
                >
                    <Stack
                        flexDirection={"row"}
                        gap={theme.spacing(1.5)}
                        alignItems={"center"}
                        sx={{
                            "@container(max-width: 300px)": {
                                flexWrap: "wrap",
                            },
                        }}
                    >
                        <Stack
                            // Yes, this is important. More info with Lucie.
                            // This is about the PropertyValue Layout.
                            width={
                                hasOption ? "100%"
                                : hasLongText ?
                                    "100%"
                                : hasSelect ?
                                    "500px"
                                :   "300px"
                            }
                        >
                            <FormTooltip title={tooltipText} arrow placement="right">
                                {/* Box is needed to make sure the `FormTooltip` can attach to it. */}
                                <Box width={"100%"}>{children}</Box>
                            </FormTooltip>
                        </Stack>
                        {unit && (
                            <Stack
                                flexDirection="row"
                                gap={theme.spacing(showsUnitConverterButton ? 2 : 0.5)}
                                alignItems="center"
                            >
                                <Typography variant="body-regular">{unit}</Typography>
                                {unitConverter && showsUnitConverterButton && (
                                    <UnitConverterButton
                                        converterKey={unitConverter.key}
                                        disabled={unitConverterDisabled}
                                        options={unitConverterOptions}
                                        unitConversion={element?.unitConversion ?? null}
                                        targetUnit={unit}
                                    />
                                )}
                            </Stack>
                        )}
                        {shouldDisplayDefaultButton && (
                            <Stack display={{ xs: "none", sm: "block" }} ml={showsUnitConverterButton ? 0.5 : 0}>
                                <DefaultValueButton onClick={layoutOptions.setDefaultValue} />
                            </Stack>
                        )}
                    </Stack>
                    {defaultValue && !isGenericValueSelected && (
                        <Stack justifyContent={"center"}>
                            <Typography
                                variant="small-regular"
                                color={theme.custom.grey[1500]}
                                display={{ xs: "none", sm: "block" }}
                            >
                                {t("valeur_par_defaut") + " " + defaultValue}
                            </Typography>

                            <Typography
                                variant="big-regular"
                                color={theme.custom.grey[1500]}
                                display={{ xs: "block", sm: "none" }}
                            >
                                {t("valeur_par_defaut") + " " + defaultValue}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <Stack display={{ xs: "none", sm: "block" }} minWidth={"auto"} textAlign={"end"}>
                    {displayScore && scoreProperty.score !== null && (
                        <FormUnit value={scoreProperty.score} unit={scoreProperty.unit} />
                    )}
                </Stack>
            </Stack>
            {unitConverter && isUnitConverterOpen && (
                <UnitConverterSection
                    converterKey={unitConverter.key}
                    property={property}
                    options={unitConverterOptions}
                    unitConversion={element?.unitConversion ?? null}
                    onApply={unitConverter.onApply}
                />
            )}
            <Stack display={{ xs: "block", sm: "none" }}>
                <Button variant="text" startIcon={<FoodsIcon size={2} icon={"reset"} />}>
                    {t("reinitialiser_la_valeur_par_defaut")}
                </Button>
            </Stack>
            <Stack flexDirection={"row"} justifyContent={"space-between"} flexWrap={"wrap"}>
                <Stack>
                    {shouldDisplayGenericButton && (
                        <GenericValueCheckbox
                            isSelected={isGenericValueSelected}
                            disabled={!canEditProperty}
                            onChange={() => layoutOptions.setGenericValue()}
                        />
                    )}
                </Stack>
                <Stack justifyContent={"center"}>
                    <UploadFileAndComment element={element} />
                </Stack>
            </Stack>
        </Stack>
    );
};
